import React, { useEffect, useRef, useState } from 'react';
import {
    AspectRatio,
    Breadcrumbs,
    Card,
    Chip,
    CircularProgress,
    Divider,
    Link,
    RadioGroup,
    Select,
    styled,
    Textarea,
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Input
} from "@mui/joy";
import Radio, { radioClasses } from '@mui/joy/Radio';
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import Option from "@mui/joy/Option";
import FormLabel from '@mui/joy/FormLabel';
import Button from "@mui/joy/Button";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    CheckCircleRounded,
    WarningAmber,
    Delete,
    AddCircleOutlineRounded as AddIcon,
    CloudUpload as CloudUploadIcon
} from "@mui/icons-material";
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import Checkbox from "@mui/joy/Checkbox";
import Sheet from "@mui/joy/Sheet";
import ListItem from "@mui/joy/ListItem";
import List from "@mui/joy/List";

import ApiService from "../../axios/Bookings-Axios";

const CANCELLATION_REASONS = [
    "Customer cancelled",
    "Employee not available",
    "Weather",
    "Customer not home",
    "Access issues",
    "Equipment failure",
    "Double booking",
    "Other"
];

interface Customer {
    notes?: string;
    full_name?: string;
    street_address?: string;
    postcode?: string;
    phone?: string;
    email?: string;
}
interface PaymentType {
    name: string;
    id?: number;
}
interface Service {
    name: string;
}
interface ServicePrice {
    price: number;
    service: Service;
}
interface Booking {
    id: number;
    customer?: Customer;
    payment_type: PaymentType | null;
    job_status: string;
    ask_for_review: boolean;
    ask_for_referral: boolean;
    send_receipt: boolean;
    send_before_after_photos: boolean;
    guarantee_type?: string | null;
    invoice_id?: string | number;
    services?: Service[];
    service_price?: ServicePrice[];
    referral_badge_label?: string;
    is_referral_reward?: boolean;
    is_referred_booking?: boolean;
}
interface BookingImage {
    id: number;
    booking: number;
    before_image: string | null;
    after_image: string | null;
    uploaded_at: string;
}

const VisuallyHiddenInput = styled('input')`
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    bottom: 0;
    left: 0;
    white-space: nowrap;
    width: 1px;
`;

function formatDate(dateInput: Date | string): string {
    let date: Date;
    if (typeof dateInput === 'string') {
        date = new Date(dateInput);
    } else {
        date = dateInput;
    }

    if (!(date instanceof Date) || isNaN(date.valueOf())) {
        const d = new Date(dateInput);
        if (isNaN(d.valueOf())) {
            console.warn("Invalid date passed to formatDate:", dateInput);
            return 'Invalid Date';
        }
        date = d;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}

export default function TodaysBookings() {
    const [loadingData, setLoadingData] = useState(true);
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [filterDate, setFilterDate] = useState<Date>(new Date());
    const [bookingImages, setBookingImages] = useState<Record<number, BookingImage[]>>({});
    const [isUploading, setIsUploading] = useState<Record<string, boolean>>({});
    const [deletingImageId, setDeletingImageId] = useState<number | null>(null);

    // Cancellation modal state
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [cancellingBookingId, setCancellingBookingId] = useState<number | null>(null);
    const [cancellationReason, setCancellationReason] = useState<string | null>(null);
    const [cancellationDetails, setCancellationDetails] = useState<string>('');

    const hasUserEditedNotes = useRef(false);
    const saveNotesTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (hasUserEditedNotes.current && !loadingData && bookings.length > 0) {
            if (saveNotesTimerRef.current) {
                clearTimeout(saveNotesTimerRef.current);
            }
            saveNotesTimerRef.current = setTimeout(async () => {
                const bookingsToProcessForNotes = [...bookings];
                let notesSaveAttempted = false;

                for (const booking of bookingsToProcessForNotes) {
                    try {
                        await ApiService.employeeUpdateBooking({
                            booking_id: booking.id,
                            notes: booking.customer?.notes,
                            payment_type: booking.payment_type?.name,
                            status: booking.job_status,
                            ask_for_review: booking.ask_for_review,
                            ask_for_referral: booking.ask_for_referral,
                            send_receipt: booking.send_receipt,
                            send_before_after_photos: booking.send_before_after_photos,
                            guarantee_type: booking.guarantee_type ?? '',
                        });
                        notesSaveAttempted = true;
                    } catch (error) {
                        console.error(`Error updating booking ${booking.id} during notes debounce:`, error);
                        toast.error(`Failed to save note changes for booking ${booking.id}.`);
                    }
                }

                if (notesSaveAttempted) {
                    toast.success('Note Changes Saved!', { position: "top-right", autoClose: 2000, theme: "dark" });
                }
                hasUserEditedNotes.current = false;
            }, 2000);
        }
        return () => {
            if (saveNotesTimerRef.current) {
                clearTimeout(saveNotesTimerRef.current);
            }
        };
    }, [bookings, loadingData]);

    useEffect(() => {
        setLoadingData(true);
        hasUserEditedNotes.current = false;

        const fetchData = async () => {
            try {
                const formattedDate = formatDate(filterDate);
                if (formattedDate === 'Invalid Date') {
                    toast.error("Invalid date selected for filtering.");
                    setBookings([]); setBookingImages({}); setLoadingData(false); return;
                }

                const fetchedBookings = await ApiService.getBookingsToday(formattedDate);

                const processedBookings = fetchedBookings.map(b => ({
                    ...b,
                    job_status: b.job_status || "",
                    ask_for_referral: b.ask_for_referral !== false,
                    send_before_after_photos: !!b.send_before_after_photos,
                }));
                setBookings(processedBookings);

                const imagesMap: Record<number, BookingImage[]> = {};
                if (Array.isArray(fetchedBookings)) {
                    for (const booking of fetchedBookings) {
                        if (booking && typeof booking.id === 'number') {
                            try {
                                const images = await ApiService.getBookingImages(booking.id);
                                imagesMap[booking.id] = images;
                            } catch (error) {
                                console.error(`Failed to load images for booking ${booking.id}.`);
                                imagesMap[booking.id] = [];
                            }
                        }
                    }
                }
                setBookingImages(imagesMap);
            } catch (error) {
                console.error('Error fetching initial bookings data:', error);
                toast.error("Failed to fetch bookings data.");
                setBookings([]); setBookingImages({});
            } finally {
                setLoadingData(false);
            }
        };
        fetchData();
    }, [filterDate]);

    const handleUploadForNewImageRecord = async (bookingId: number, files: FileList | null, imageType: 'before' | 'after') => {
        if (!files || files.length === 0) return;
        if (typeof bookingId !== 'number') {
            toast.error('Cannot upload images: Invalid booking ID.'); return;
        }
        const uploadKey = `new_${bookingId}_${imageType}`;
        setIsUploading(prev => ({ ...prev, [uploadKey]: true }));
        try {
            const formData = new FormData();
            for (let i = 0; i < files.length; i++) {
                formData.append(imageType === 'before' ? 'before_image' : 'after_image', files[i]);
            }

            const newImageRecords = await ApiService.postBookingImages(bookingId, formData);

            setBookingImages(prev => {
                const currentImages = prev[bookingId] || [];
                return { ...prev, [bookingId]: [...currentImages, ...newImageRecords] };
            });
            toast.success(`New '${imageType}' image(s) added successfully!`);
        } catch (error) {
            console.error(`Error adding new ${imageType} image(s):`, error);
            toast.error(`Failed to add new '${imageType}' image(s). See console.`);
        } finally {
            setIsUploading(prev => ({ ...prev, [uploadKey]: false }));
        }
    };

    const handleUploadToExistingImageRecord = async (bookingId: number, imageRecordId: number, files: FileList | null, imageTypeToUpload: 'before' | 'after') => {
        if (!files || files.length === 0) return;
        if (typeof bookingId !== 'number' || typeof imageRecordId !== 'number') {
            toast.error('Cannot upload: Invalid booking or image record ID.'); return;
        }
        const uploadKey = `update_${bookingId}_${imageRecordId}_${imageTypeToUpload}`;
        setIsUploading(prev => ({ ...prev, [uploadKey]: true }));
        try {
            const formData = new FormData();
            for (let i = 0; i < files.length; i++) {
                formData.append(imageTypeToUpload === 'before' ? 'before_image' : 'after_image', files[i]);
            }

            const updatedImageRecord = await ApiService.updateBookingImageFile(imageRecordId, formData);

            setBookingImages(prev => {
                const currentImagesForBooking = prev[bookingId] || [];
                const updatedImagesList = currentImagesForBooking.map(img =>
                    img.id === imageRecordId ? updatedImageRecord : img
                );
                return { ...prev, [bookingId]: updatedImagesList };
            });
            toast.success(`Image set (ID: ${imageRecordId}) updated successfully with '${imageTypeToUpload}' image!`);
        } catch (error) {
            console.error(`Error updating image set ${imageRecordId}:`, error);
            toast.error(`Failed to update image set (ID: ${imageRecordId}). See console.`);
        } finally {
            setIsUploading(prev => ({ ...prev, [uploadKey]: false }));
        }
    };

    const handleDeleteImage = async (bookingId: number, imageId: number) => {
        if (deletingImageId === imageId) return;
        const confirmDelete = window.confirm("Are you sure you want to delete this image set (Before & After if present)? This action cannot be undone.");
        if (!confirmDelete) {
            return;
        }

        setDeletingImageId(imageId);
        try {
            await ApiService.deleteBookingImage(imageId);

            setBookingImages(prev => {
                const currentImagesForBooking = prev[bookingId] || [];
                const updatedImagesForBooking = currentImagesForBooking.filter(img => img.id !== imageId);
                return { ...prev, [bookingId]: updatedImagesForBooking };
            });
            toast.success(`Image set (ID: ${imageId}) deleted successfully.`);
        } catch (error) {
            console.error(`Error deleting image ${imageId}:`, error);
            toast.error(`Failed to delete image set (ID: ${imageId}). See console for details.`);
        } finally {
            setDeletingImageId(null);
        }
    };

    const instantSaveBooking = async (bookingData: Booking, fieldName: string) => {
        if(!bookingData.payment_type && bookingData.job_status == "Completed"){
            toast.error(`Please select a payment type for booking ${bookingData.id}.`, { autoClose: 1500, theme: "dark" });
            return;
        }
        try {
            await ApiService.employeeUpdateBooking({
                booking_id: bookingData.id,
                notes: bookingData.customer?.notes,
                status: bookingData.job_status,
                ask_for_review: bookingData.ask_for_review,
                ask_for_referral: bookingData.ask_for_referral,
                send_receipt: bookingData.send_receipt,
                send_before_after_photos: bookingData.send_before_after_photos,
                guarantee_type: bookingData.guarantee_type ?? '',
                ...(bookingData.payment_type && { payment_type: bookingData.payment_type.name })
            });
            toast.success(`${fieldName} for booking ${bookingData.id} updated!`, { autoClose: 1500, theme: "dark" });
        } catch (error) {
            console.error(`Error instantly updating ${fieldName} for booking ${bookingData.id}:`, error);
            toast.error(`Failed to update ${fieldName} for booking ${bookingData.id}.`);
        }
    };

    const handlePaymentTypeChange = (bookingId: number, value: string | null) => {
        if(value == null){return}
        let bookingToUpdateInstantly: Booking | undefined;
        setBookings(prevBookings =>
            prevBookings.map(booking => {
                if (booking.id === bookingId) {
                    const updatedBooking = { ...booking, payment_type: value ? { name: value } : null };
                    bookingToUpdateInstantly = updatedBooking;
                    return updatedBooking;
                }
                return booking;
            })
        );
        if (bookingToUpdateInstantly) {
            instantSaveBooking(bookingToUpdateInstantly, "Payment Type");
        }
    };

    const handleAskForReviewChange = (bookingId: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.checked;
        let bookingToUpdateInstantly: Booking | undefined;
        setBookings(prevBookings =>
            prevBookings.map(booking => {
                if (booking.id === bookingId) {
                    const updatedBooking = { ...booking, ask_for_review: value };
                    bookingToUpdateInstantly = updatedBooking;
                    return updatedBooking;
                }
                return booking;
            })
        );
        if (bookingToUpdateInstantly) {
            instantSaveBooking(bookingToUpdateInstantly, "Ask for Review");
        }
    };

    const handleAskForReferralChange = (bookingId: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.checked;
        let bookingToUpdateInstantly: Booking | undefined;
        setBookings(prevBookings =>
            prevBookings.map(booking => {
                if (booking.id === bookingId) {
                    const updatedBooking = { ...booking, ask_for_referral: value };
                    bookingToUpdateInstantly = updatedBooking;
                    return updatedBooking;
                }
                return booking;
            })
        );
        if (bookingToUpdateInstantly) {
            instantSaveBooking(bookingToUpdateInstantly, "Ask for Referral");
        }
    };

    const handleSendReceiptChange = (bookingId: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.checked;
        let bookingToUpdateInstantly: Booking | undefined;
        setBookings(prevBookings =>
            prevBookings.map(booking => {
                if (booking.id === bookingId) {
                    const updatedBooking = { ...booking, send_receipt: value };
                    bookingToUpdateInstantly = updatedBooking;
                    return updatedBooking;
                }
                return booking;
            })
        );
        if (bookingToUpdateInstantly) {
            instantSaveBooking(bookingToUpdateInstantly, "Send Receipt");
        }
    };

    const handleSendBeforeAfterPhotosChange = (bookingId: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.checked;
        let bookingToUpdateInstantly: Booking | undefined;
        setBookings(prevBookings =>
            prevBookings.map(booking => {
                if (booking.id === bookingId) {
                    const updatedBooking = { ...booking, send_before_after_photos: value };
                    bookingToUpdateInstantly = updatedBooking;
                    return updatedBooking;
                }
                return booking;
            })
        );
        if (bookingToUpdateInstantly) {
            instantSaveBooking(bookingToUpdateInstantly, "Send Before/After Photos Email");
        }
    };

    const handleGuaranteeTypeChange = (bookingId: number, value: string | null) => {
        let bookingToUpdateInstantly: Booking | undefined;
        setBookings(prevBookings =>
            prevBookings.map(booking => {
                if (booking.id === bookingId) {
                    const updatedBooking = { ...booking, guarantee_type: value || null };
                    bookingToUpdateInstantly = updatedBooking;
                    return updatedBooking;
                }
                return booking;
            })
        );
        if (bookingToUpdateInstantly) {
            instantSaveBooking(bookingToUpdateInstantly, "Guarantee Type");
        }
    };

    const handleNotesChange = (bookingId: number) => (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        hasUserEditedNotes.current = true;
        const { value } = event.target;
        setBookings(prevBookings =>
            prevBookings.map(booking =>
                booking.id === bookingId
                    ? {
                        ...booking,
                        customer: {
                            ...(booking.customer || {}),
                            notes: value,
                        } as Customer,
                    }
                    : booking
            )
        );
    };

    const handleJobStatusChange = (bookingId: number, status: string) => {
        // If cancelling, open the cancellation modal instead of immediately updating
        if (status === 'Mark As Cancelled') {
            setCancellingBookingId(bookingId);
            setCancellationReason(null);
            setCancellationDetails('');
            setCancelModalOpen(true);
            return;
        }

        if (status === 'Mark As Complete') {
            const imageSets = bookingImages[bookingId] || [];
            const hasUnpairedImageSet = imageSets.some(
                (imageSet) => Boolean(imageSet.before_image) !== Boolean(imageSet.after_image)
            );

            if (hasUnpairedImageSet) {
                toast.error(
                    "Each before photo must have a matching after photo before marking this booking as complete.",
                    { autoClose: 3500, theme: "dark" }
                );
                return;
            }
        }

        let bookingToUpdateInstantly: Booking | undefined;
        setBookings(prevBookings =>
            prevBookings.map(booking => {
                if (booking.id === bookingId) {
                    const updatedBooking = { ...booking, job_status: status };
                    bookingToUpdateInstantly = updatedBooking;
                    return updatedBooking;
                }
                return booking;
            })
        );
        if (bookingToUpdateInstantly) {
            instantSaveBooking(bookingToUpdateInstantly, "Job Status");
        }
    };

    const handleCancelModalClose = () => {
        setCancelModalOpen(false);
        setCancellingBookingId(null);
        setCancellationReason(null);
        setCancellationDetails('');
    };

    const handleConfirmCancellation = async () => {
        if (!cancellingBookingId || !cancellationReason || !cancellationDetails.trim()) {
            toast.error('Please select a reason and provide details before cancelling.', { autoClose: 3000, theme: "dark" });
            return;
        }

        const fullCancellationReason = `${cancellationReason}: ${cancellationDetails.trim()}`;
        
        let bookingToUpdateInstantly: Booking | undefined;
        setBookings(prevBookings =>
            prevBookings.map(booking => {
                if (booking.id === cancellingBookingId) {
                    const updatedBooking = { ...booking, job_status: 'Mark As Cancelled' };
                    bookingToUpdateInstantly = updatedBooking;
                    return updatedBooking;
                }
                return booking;
            })
        );

        if (bookingToUpdateInstantly) {
            try {
                await ApiService.employeeUpdateBooking({
                    booking_id: bookingToUpdateInstantly.id,
                    notes: bookingToUpdateInstantly.customer?.notes,
                    status: 'Mark As Cancelled',
                    ask_for_review: bookingToUpdateInstantly.ask_for_review,
                    ask_for_referral: bookingToUpdateInstantly.ask_for_referral,
                    send_receipt: bookingToUpdateInstantly.send_receipt,
                    send_before_after_photos: bookingToUpdateInstantly.send_before_after_photos,
                    guarantee_type: bookingToUpdateInstantly.guarantee_type ?? '',
                    cancellation_reason: fullCancellationReason,
                    ...(bookingToUpdateInstantly.payment_type && { payment_type: bookingToUpdateInstantly.payment_type.name })
                });
                toast.success(`Booking ${cancellingBookingId} cancelled successfully!`, { autoClose: 1500, theme: "dark" });
            } catch (error) {
                console.error(`Error cancelling booking ${cancellingBookingId}:`, error);
                toast.error(`Failed to cancel booking ${cancellingBookingId}.`);
            }
        }

        handleCancelModalClose();
    };

    const isCancellationValid = cancellationReason && cancellationDetails.trim().length > 0;

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} theme="dark" newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
            <Box sx={{display: 'flex', alignItems: 'center'}}>
                <Breadcrumbs size="sm" aria-label="breadcrumbs" separator={<ChevronRightRoundedIcon/>} sx={{pl: 0}}>
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home"><HomeRoundedIcon/></Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>Dashboard</Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>Bookings for {formatDate(filterDate)}</Typography>
                </Breadcrumbs>
            </Box>
            <Box sx={{ display: 'flex', mb: 1, gap: 1, flexDirection: {xs: 'column', sm: 'row'}, alignItems: {xs: 'start', sm: 'center'}, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <Typography level="h2" component="h1">Bookings for {formatDate(filterDate)}</Typography>
            </Box>
            <Box sx={{width: 'fit-content', mb: 2}}>
                <FormLabel>Pick a date</FormLabel>
                <DatePicker selected={filterDate} dateFormat="dd/MM/yyyy" onChange={(date: Date | null) => { if (date) setFilterDate(date); }} className="joy-date-picker" />
            </Box>

            {loadingData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}><CircularProgress size="lg" /></Box>
            ) : (
                <Box sx={{ flexGrow: 1,  }}>
                    {bookings.length === 0 ? (
                        <Typography startDecorator={<WarningAmber/>} level={"title-sm"} sx={{marginTop: '10px', textAlign: 'center'}}>No bookings on {formatDate(filterDate)}</Typography>
                    ) : (
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, pb: '100px'}}>
                            {bookings.map((booking) => (
                                <Card key={booking.id} sx={{ flex: '1 1 calc(50% - 16px)', minWidth: { xs: '100%', sm: '350px', md: '400px' }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box>
                                        <FormLabel>Customer Information</FormLabel>
                                        <Typography level={"title-md"}>Name: <Typography fontWeight={350}>{booking?.customer?.full_name || 'N/A'}</Typography></Typography>
                                        <Typography noWrap title={`${booking?.customer?.street_address || 'N/A'}, ${booking?.customer?.postcode || 'N/A'}`} level={"title-md"}>Address: <Typography fontWeight={350}>{booking?.customer?.street_address || 'N/A'}, {booking?.customer?.postcode || 'N/A'}</Typography></Typography>
                                        <Typography level={"title-md"}>Phone: <Typography fontWeight={350}>{booking?.customer?.phone || 'N/A'}</Typography></Typography>
                                        <Typography noWrap title={booking?.customer?.email || 'N/A'} level={"title-md"}>Email: <Typography fontWeight={350}>{booking?.customer?.email || 'N/A'}</Typography></Typography>
                                    </Box>
                                    <Box>
                                        <FormLabel>Notes</FormLabel>
                                        <Textarea name="notes" value={booking?.customer?.notes || ''} onChange={handleNotesChange(booking.id)} sx={{background: 'var(--joy-palette-background-body)'}} variant="outlined" minRows={3} maxRows={3}/>
                                    </Box>
                                    <Divider/>
                                    <FormLabel>Booking Information</FormLabel>
                                    {booking.referral_badge_label && (
                                        <Chip color="primary" variant="soft" size="sm" sx={{ alignSelf: 'flex-start' }}>
                                            {booking.referral_badge_label}
                                        </Chip>
                                    )}
                                    <Box sx={{display: 'flex', alignItems: 'center', mt: 0.5}}>
                                        <Typography sx={{marginRight: '5px'}} level={"title-md"}>Invoice Sent:</Typography>
                                        {booking?.invoice_id ? (<Chip color={"success"} size="sm">ID: #{booking?.invoice_id}</Chip>) : (<Chip color={"danger"} size="sm">No</Chip>)}
                                    </Box>
                                    <Typography noWrap title={booking?.services?.map(s => s.name).join(', ') || 'N/A'} level={"title-md"}>
                                        Services:{" "}
                                        <Typography fontWeight={350}>
                                            {booking?.services?.length
                                                ? booking.services
                                                    .map(s => s.name)
                                                    .join(', ')
                                                    .replace(/, ([^,]*)$/, ' and $1')
                                                : 'N/A'}
                                        </Typography>
                                    </Typography>
                                    <Typography noWrap level={"title-md"}>
                                        Price:{" "}
                                        <Typography fontWeight={350}>
                                            £
                                            {booking?.service_price
                                                ?.filter(sp =>
                                                    booking?.services?.some(s => s.name === sp.service.name)
                                                )
                                                ?.reduce((total, sp) => total + sp.price, 0) || 0}
                                        </Typography>
                                        {booking.is_referral_reward && (
                                            <Chip color="primary" variant="solid" size="sm" sx={{ ml: 1 }}>
                                                Free referral reward
                                            </Chip>
                                        )}
                                    </Typography>
                                    <Divider/>
                                    <Box>
                                        <FormLabel>Before / After Images</FormLabel>
                                        <List sx={{gap: 1.5, borderRadius: 'sm', background: 'var(--joy-palette-background-level1)', p:1, width: '100%'}}>
                                            {(bookingImages[booking.id]?.length || 0) > 0 ? bookingImages[booking.id]?.map((imageRecord) => {
                                                const beforeUploadKey = `update_${booking.id}_${imageRecord.id}_before`;
                                                const afterUploadKey = `update_${booking.id}_${imageRecord.id}_after`;
                                                const isDeletingThisImage = deletingImageId === imageRecord.id;
                                                return (
                                                    <ListItem key={imageRecord.id} sx={{p:0, width: '100%'}}>
                                                        <Sheet variant="outlined" sx={{ width: '100%', p: 1.5, borderRadius: 'sm', display: 'flex', flexDirection: 'column', gap: 1}}>
                                                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5}}>
                                                                <Typography level="body-sm">Image Set (ID: {imageRecord.id})</Typography>
                                                                <Button
                                                                    variant="plain"
                                                                    color="danger"
                                                                    size="sm"
                                                                    startDecorator={isDeletingThisImage ? <CircularProgress size="sm" color="danger"/> : <Delete />}
                                                                    onClick={() => handleDeleteImage(booking.id, imageRecord.id)}
                                                                    disabled={isDeletingThisImage || !!isUploading[beforeUploadKey] || !!isUploading[afterUploadKey]}
                                                                >
                                                                    {isDeletingThisImage ? 'Deleting...' : 'Delete'}
                                                                </Button>
                                                            </Box>
                                                            <Box sx={{display: 'flex', gap: 1.5, flexWrap: 'nowrap' }}>
                                                                <Box sx={{flex: 1, minWidth: 0}}>
                                                                    <Typography level="body-xs" sx={{mb: 0.5}}>Before</Typography>
                                                                    {imageRecord.before_image ? (
                                                                        <AspectRatio ratio="16/9" objectFit="cover" sx={{ borderRadius: 'xs', maxWidth: '100%' }}>
                                                                            <img src={imageRecord.before_image} alt={`Booking ${booking.id} - Before (ID: ${imageRecord.id})`} />
                                                                        </AspectRatio>
                                                                    ) : (
                                                                        isUploading[beforeUploadKey] ?
                                                                            <Box sx={{display:'flex', alignItems:'center', justifyContent:'center', height:{xs:'80px', sm:'100px'}, border:'1px dashed', borderColor:'neutral.outlinedBorder', borderRadius:'xs'}}><CircularProgress size="sm"/></Box> :
                                                                            <Button
                                                                                component="label" variant="outlined" fullWidth disabled={!!isUploading[beforeUploadKey] || isDeletingThisImage}
                                                                                sx={{height: {xs:'80px', sm:'100px'}, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', p:1, borderColor: 'neutral.outlinedBorder', borderStyle: 'dashed', '&:hover': {bgcolor: 'action.hover'}}}
                                                                            >
                                                                                <CloudUploadIcon sx={{mb:0.5}}/> <Typography level="body-xs">Add Before</Typography>
                                                                                <VisuallyHiddenInput type="file" accept="image/*,video/*" onChange={(e) => handleUploadToExistingImageRecord(booking.id, imageRecord.id, e.target.files, 'before')} />
                                                                            </Button>
                                                                    )}
                                                                </Box>
                                                                <Box sx={{flex: 1, minWidth: 0}}>
                                                                    <Typography level="body-xs" sx={{mb: 0.5}}>After</Typography>
                                                                    {imageRecord.after_image ? (
                                                                        <AspectRatio ratio="16/9" objectFit="cover" sx={{ borderRadius: 'xs', maxWidth: '100%' }}>
                                                                            <img src={imageRecord.after_image} alt={`Booking ${booking.id} - After (ID: ${imageRecord.id})`} />
                                                                        </AspectRatio>
                                                                    ) : (
                                                                        isUploading[afterUploadKey] ?
                                                                            <Box sx={{display:'flex', alignItems:'center', justifyContent:'center', height:{xs:'80px', sm:'100px'}, border:'1px dashed', borderColor:'neutral.outlinedBorder', borderRadius:'xs'}}><CircularProgress size="sm"/></Box> :
                                                                            <Button
                                                                                component="label" variant="outlined" fullWidth disabled={!!isUploading[afterUploadKey] || isDeletingThisImage}
                                                                                sx={{height: {xs:'80px', sm:'100px'}, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', p:1, borderColor: 'neutral.outlinedBorder', borderStyle: 'dashed', '&:hover': {bgcolor: 'action.hover'}}}
                                                                            >
                                                                                <CloudUploadIcon sx={{mb:0.5}}/> <Typography level="body-xs">Add After</Typography>
                                                                                <VisuallyHiddenInput type="file" accept="image/*,video/*" onChange={(e) => handleUploadToExistingImageRecord(booking.id, imageRecord.id, e.target.files, 'after')} />
                                                                            </Button>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Sheet>
                                                    </ListItem>
                                                );
                                            }) : (
                                                <></>
                                            )}
                                            <ListItem sx={{p:0, mt: ((bookingImages[booking.id]?.length || 0) > 0 ? 1 : 0), width: '100%'}}>
                                                <Sheet variant="soft" sx={{ width: '100%', p: 1, borderRadius: 'sm', display: 'flex', flexDirection: {xs: 'column', sm:'row'}, gap: 1}}>
                                                    <Button
                                                        component="label" variant="outlined" color="primary" fullWidth startDecorator={!isUploading[`new_${booking.id}_before`] ? <AddIcon/> : null}
                                                        loading={!!isUploading[`new_${booking.id}_before`]} disabled={!!isUploading[`new_${booking.id}_before`] || !!isUploading[`new_${booking.id}_after`]}
                                                    >
                                                        Add New 'Before'
                                                        <VisuallyHiddenInput type="file" multiple accept="image/*,video/*" onChange={(e) => handleUploadForNewImageRecord(booking.id, e.target.files, 'before')} />
                                                    </Button>
                                                    <Button
                                                        component="label" variant="outlined" color="primary" fullWidth startDecorator={!isUploading[`new_${booking.id}_after`] ? <AddIcon/> : null}
                                                        loading={!!isUploading[`new_${booking.id}_after`]} disabled={!!isUploading[`new_${booking.id}_after`] || !!isUploading[`new_${booking.id}_before`]}
                                                    >
                                                        Add New 'After'
                                                        <VisuallyHiddenInput type="file" multiple accept="image/*,video/*" onChange={(e) => handleUploadForNewImageRecord(booking.id, e.target.files, 'after')} />
                                                    </Button>
                                                </Sheet>
                                            </ListItem>
                                        </List>
                                    </Box>
                                    <Divider/>
                                    <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                        <FormLabel>Emails / Text Confirmations (Sent on 'Complete')</FormLabel>
                                        <Checkbox checked={booking.send_before_after_photos || false} color="primary" variant="soft" label="Send Before / After Photos via Email" onChange={(event) => handleSendBeforeAfterPhotosChange(booking.id, event)}/>
                                        <Checkbox checked={booking.ask_for_review || false} color="primary" variant="soft" label="Ask Customer For Review" onChange={(event) => handleAskForReviewChange(booking.id, event)}/>
                                        <Checkbox checked={booking.ask_for_referral !== false} color="primary" variant="soft" label="Ask Customer For Referral" onChange={(event) => handleAskForReferralChange(booking.id, event)}/>
                                        <Checkbox checked={booking.send_receipt || false} color="primary" variant="soft" label="Send Receipt" onChange={(event) => handleSendReceiptChange(booking.id, event)}/>
                                        <Checkbox defaultChecked disabled color="primary" variant="soft" label="Send Gutter Certificate Email (Auto)"/>
                                        <Checkbox defaultChecked disabled color="primary" variant="soft" label="Send Confirmation Text (Auto)"/>
                                    </Box>
                                    {booking.services?.some(s => s.name.toLowerCase() === 'roof clean') && (
                                        <Box>
                                            <FormLabel>Roof Clean Guarantee Type</FormLabel>
                                            <Select
                                                placeholder="Select guarantee type…"
                                                value={booking.guarantee_type || ''}
                                                onChange={(_event, newValue) => handleGuaranteeTypeChange(booking.id, newValue)}
                                                sx={{background: 'var(--joy-palette-background-body)'}}
                                            >
                                                <Option value="2_year">2 Year (Biocide Treatment)</Option>
                                                <Option value="10_year">10 Year (Biocide Treatment & Sealant)</Option>
                                            </Select>
                                        </Box>
                                    )}
                                    <Box>
                                        <FormLabel>Payment Type</FormLabel>
                                        <Select
                                            placeholder="Select payment type…"
                                            value={booking.payment_type?.name || ''}
                                            onChange={(_event, newValue) => handlePaymentTypeChange(booking.id, newValue)}
                                            sx={{background: 'var(--joy-palette-background-body)'}}
                                        >
                                            <Option value="Cash">Cash</Option>
                                            <Option value="Card">Card</Option>
                                            <Option value="Invoice">Invoice</Option>
                                        </Select>
                                    </Box>
                                    <Divider/>
                                    <Box>
                                        <FormLabel>Change Status</FormLabel>
                                        <RadioGroup
                                            overlay
                                            value={booking.job_status || ''}
                                            name={`status-${booking.id}`}
                                            sx={{ flexDirection: 'row', gap: 1, flexWrap: 'wrap', [`& .${radioClasses.checked}`]: { [`& .${radioClasses.action}`]: { inset: -1, border: '2px solid', borderColor: 'primary.500', }, }, [`& .${radioClasses.radio}`]: { display: 'contents', '& > svg': { zIndex: 2, position: 'absolute', top: '-0.25rem', right: '-0.25rem', bgcolor: 'background.surface', borderRadius: '50%', p: '1px' }, }, }}
                                        >
                                            {['Mark As Complete', 'Mark As Needs Rescheduling', 'Mark As Cancelled']
                                                .filter(statusValue => statusValue !== 'Mark As Complete' || (booking.payment_type?.name && booking.payment_type.name !== ''))
                                                .map((statusValue) => (
                                                    <Sheet
                                                        key={statusValue}
                                                        onClick={() => handleJobStatusChange(booking.id, statusValue)}
                                                        variant="outlined"
                                                        sx={{ borderRadius: 'md', boxShadow: 'sm', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, p: 1, flex: '1 1 auto', minWidth: '100px', cursor: 'pointer', textAlign: 'center', background: 'var(--joy-palette-background-body)' }}
                                                    >
                                                        <Radio value={statusValue} checkedIcon={<CheckCircleRounded />} sx={{mt:-0.5, mb:0.25}} />
                                                        <Typography level="body-xs">{statusValue}</Typography>
                                                    </Sheet>
                                                ))}
                                        </RadioGroup>
                                    </Box>
                                </Card>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {/* Cancellation Reason Modal */}
            <Modal open={cancelModalOpen} onClose={handleCancelModalClose}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ 
                    minWidth: { xs: 'auto', sm: 400 },
                    width: { xs: '95vw', sm: 'auto' },
                    maxWidth: { xs: '95vw', sm: '500px' },
                }}>
                    <DialogTitle>
                        <WarningRoundedIcon />
                        Cancel Booking
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography level="body-md">
                            Please provide a reason for cancelling this booking.
                        </Typography>
                        
                        <Box>
                            <FormLabel>Cancellation Reason</FormLabel>
                            <Select
                                placeholder="Select a reason..."
                                value={cancellationReason}
                                onChange={(_event, newValue) => setCancellationReason(newValue)}
                                sx={{ width: '100%' }}
                            >
                                {CANCELLATION_REASONS.map((reason) => (
                                    <Option key={reason} value={reason}>{reason}</Option>
                                ))}
                            </Select>
                        </Box>

                        <Box>
                            <FormLabel>Additional Details</FormLabel>
                            <Textarea
                                placeholder="Please provide more details about the cancellation..."
                                value={cancellationDetails}
                                onChange={(e) => setCancellationDetails(e.target.value)}
                                minRows={3}
                                maxRows={5}
                                sx={{ width: '100%' }}
                            />
                        </Box>

                        {(!cancellationReason || !cancellationDetails.trim()) && (
                            <Typography level="body-sm" color="warning" startDecorator={<WarningAmber sx={{ fontSize: 16 }} />}>
                                Both reason and details are required to cancel.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleConfirmCancellation}
                            disabled={!isCancellationValid}
                        >
                            Confirm Cancellation
                        </Button>
                        <Button variant="plain" color="neutral" onClick={handleCancelModalClose}>
                            Go Back
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </>
    );
}